/**
 * AuthScreen - the way in, for a player and for the GM.
 *
 * A PLAYER NEVER SEES A CREDENTIAL: they get an identity the server can
 * check, then type a name and a code. The GM is the only person whose
 * identity has to survive a browser wipe, and the only one who signs in -
 * behind "Sign in as GM", unless there is no player route, in which case the
 * GM's options show outright (a Google-only deployment would otherwise have
 * no primary action). WHAT THIS DEPLOYMENT OFFERS is asked of the backend;
 * this screen only renders the answer. The GM's form state lives in the app
 * (`useGmSignInForm`), so it survives a trip to another screen as it did.
 * Extracted from App; the markup is unchanged.
 */
import { useState } from 'react';
import { backend } from '../../backend/app';
import clsx from '../../utils/clsx';
import styles from '../../styles/App.module.css';

/** The GM sign-in form's state: revealed or not, and what has been typed. */
export function useGmSignInForm() {
  const [gmSignIn, setGmSignIn] = useState(false);
  const [gmIdentity, setGmIdentity] = useState('');
  const [gmPassword, setGmPassword] = useState('');
  return { gmSignIn, setGmSignIn, gmIdentity, setGmIdentity, gmPassword, setGmPassword };
}

/**
 * EMAIL, because that is the only identity the server will match (the users
 * collection's identityFields is ['email']). It said "Username", and anyone
 * not handed the exact string typed a name and was refused. The address need
 * not be a real mailbox - nothing is sent to it - but it must be email-SHAPED.
 */
function PasswordForm({ form, authBusy, onSubmit }) {
  return (
    <form className={styles.nameSection} onSubmit={onSubmit}>
      <input
        className={styles.inputField}
        value={form.gmIdentity}
        onChange={(e) => form.setGmIdentity(e.target.value)}
        placeholder="Email"
        type="email"
        autoComplete="username"
        aria-label="Email"
      />
      <input
        className={styles.inputField}
        type="password"
        value={form.gmPassword}
        onChange={(e) => form.setGmPassword(e.target.value)}
        placeholder="Password"
        autoComplete="current-password"
        aria-label="Password"
      />
      <button className={styles.btnPrimary} type="submit" disabled={authBusy}>
        {authBusy ? 'Signing in…' : 'Sign In'}
      </button>
    </form>
  );
}

/** The GM's routes: revealed on request, or outright when players have none. */
function GmOptions({ methods, show, form, authBusy, onGoogleSignIn, onPasswordSubmit }) {
  const offersGm = methods.includes('google') || methods.includes('password');
  return (
    <>
      {!show && offersGm && (
        <button className={styles.btnSecondary} onClick={() => form.setGmSignIn(true)}>
          Sign in as GM
        </button>
      )}
      {show && methods.includes('google') && (
        <button className={styles.btnPrimary} onClick={onGoogleSignIn} disabled={authBusy}>
          Sign In with Google
        </button>
      )}
      {show && methods.includes('password') && (
        <PasswordForm form={form} authBusy={authBusy} onSubmit={onPasswordSubmit} />
      )}
    </>
  );
}

/**
 * @param {object} props
 * @param {object} props.form - from useGmSignInForm
 * @param {boolean} props.authBusy
 * @param {Function} props.onPlay - the player's way in
 * @param {Function} props.onGoogleSignIn - the GM's popup; its failures are the handler's to say
 * @param {Function} props.onPasswordSignIn - (event, { identity, password, clearPassword })
 */
export default function AuthScreen({ form, authBusy, onPlay, onGoogleSignIn, onPasswordSignIn }) {
  const methods = backend.auth.methods;
  const canPlay = methods.includes('anonymous');
  const showGmOptions = form.gmSignIn || !canPlay;
  const onPasswordSubmit = (e) =>
    onPasswordSignIn(e, {
      identity: form.gmIdentity,
      password: form.gmPassword,
      clearPassword: () => form.setGmPassword(''),
    });
  return (
    <div className={styles.authScreen}>
      <h1 className={clsx(styles.title, styles.titleAuth)}>DeathWatch Combat Simulator</h1>
      <p className={styles.subtitle}>
        {canPlay ? 'Joining a game? You need a name and a code - nothing else.' : 'Please sign in to continue.'}
      </p>
      <div className={styles.stackedActions}>
        {canPlay && (
          <button className={styles.btnPrimary} onClick={onPlay} disabled={authBusy}>
            {authBusy ? 'One moment…' : 'Join a Game'}
          </button>
        )}
        <GmOptions
          methods={methods}
          show={showGmOptions}
          form={form}
          authBusy={authBusy}
          onGoogleSignIn={onGoogleSignIn}
          onPasswordSubmit={onPasswordSubmit}
        />
      </div>
    </div>
  );
}
